import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, RefreshCw, Trash2, X } from 'lucide-react';
import { apiErrorMessage } from '../utils/apiError';
import { useCkScope, scopeKey, readCkScope, requestConfig, Field, Form, Table, decimalProps, commandId } from './CkOperationsShared';
import { wasteApi, WASTE_REASONS, REASON_LABELS, SOURCE_LABELS, money, qtyByUom } from './WasteShared';
import './CkOperations.css';

const message = e => apiErrorMessage(e.response?.data?.detail, e.message || 'Request failed.');
const isoDay = d => d.toISOString().slice(0, 10);

export default function Waste() {
  const scope = useCkScope();
  return <WasteWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function RecordWaste({ run, busy, canSeeValue }) {
  const blank = { target: 'lot', lot_id: '', ingredient_id: '', qty: '', uom: '', reason: 'spoiled', note: '', photo_ref: '' };
  const [form, setForm] = useState(blank);
  const [wasteId, setWasteId] = useState(commandId);
  const set = key => e => setForm({ ...form, [key]: e.target.value });
  const submit = async () => {
    const body = { waste_id: wasteId, qty: form.qty, reason: form.reason };
    if (form.target === 'lot') body.lot_id = form.lot_id.trim(); else { body.ingredient_id = form.ingredient_id.trim(); body.uom = form.uom.trim(); }
    if (form.target === 'lot' && form.uom.trim()) body.uom = form.uom.trim();
    if (form.note.trim()) body.note = form.note.trim();
    if (form.photo_ref.trim()) body.photo_ref = form.photo_ref.trim();
    const saved = await run('post', '/records', body);
    if (saved) { setForm(blank); setWasteId(commandId()); }
  };
  return <Form title="Record waste" submit="Record waste" busy={busy} onSubmit={submit}>
    <Field label="Waste against" value={form.target} onChange={set('target')} options={[{ value: 'lot', label: 'A lot' }, { value: 'ingredient', label: 'An ingredient (no lot)' }]} />
    {form.target === 'lot'
      ? <Field label="Lot ID" required value={form.lot_id} onChange={set('lot_id')} />
      : <Field label="Ingredient ID" required value={form.ingredient_id} onChange={set('ingredient_id')} />}
    <Field label="Waste quantity" required value={form.qty} onChange={set('qty')} {...decimalProps} />
    <Field label="Unit of measure" required={form.target === 'ingredient'} placeholder={form.target === 'lot' ? 'Lot unit' : 'kg'} value={form.uom} onChange={set('uom')} />
    <Field label="Reason" value={form.reason} onChange={set('reason')} options={WASTE_REASONS} />
    <Field label="Note" required={form.reason === 'other'} maxLength={500} value={form.note} onChange={set('note')} />
    <Field label="Photo reference (optional)" maxLength={500} value={form.photo_ref} onChange={set('photo_ref')} />
    {canSeeValue && <p className="ck-empty">Value is computed from the lot's unit cost, or the supplier price for an ingredient.</p>}
  </Form>;
}

export function ProposedQueue({ rows, canDecide, busy, decide, canSeeValue }) {
  return <section aria-label="Proposed waste"><h2>Proposed waste (expiry sweep)</h2>
    <Table headers={['Lot', 'Item', 'Quantity', ...(canSeeValue ? ['Value'] : []), 'Note', 'Actions']} empty={!rows.length}>
      {rows.map(r => <tr key={r.waste_id}><td>{r.lot_id || '-'}</td><td>{r.item_id}</td><td className="ck-number">{r.qty} {r.uom}</td>
        {canSeeValue && <td className="ck-number">{money(r.value)}</td>}<td>{r.note || '-'}</td>
        <td><div className="ck-actions">{canDecide && <>
          <button disabled={busy} aria-label={`Confirm ${r.waste_id}`} onClick={() => decide(r.waste_id, 'confirm')}><Check size={16} />Confirm</button>
          <button disabled={busy} aria-label={`Reject ${r.waste_id}`} onClick={() => decide(r.waste_id, 'reject')}><X size={16} />Reject</button></>}</div></td></tr>)}
    </Table></section>;
}

export function BarList({ title, groups, labels = {} }) {
  const values = groups.map(g => Number(g.value) || 0);   // bar widths only; amounts shown are the exact strings
  const max = Math.max(0, ...values);
  return <figure className="waste-chart" aria-label={title}><figcaption><h3>{title}</h3></figcaption>
    {groups.length === 0 ? <p className="ck-empty">No waste in this range.</p> : groups.map((g, i) => <div key={g.key} className="waste-bar-row">
      <span className="waste-bar-label">{labels[g.key] || g.key}</span>
      <span className="waste-bar-track"><span className="waste-bar" style={{ width: `${max > 0 ? Math.max(2, (values[i] / max) * 100) : 0}%`, display: 'inline-block', height: '0.75rem', background: 'currentColor' }} /></span>
      <span className="ck-number">{money(g.value)}{g.unknown_value_count ? ` (+${g.unknown_value_count} unvalued)` : ''}</span>
    </div>)}
  </figure>;
}

function GroupTable({ title, groups, labels = {} }) {
  return <section aria-label={title}><h3>{title}</h3><Table headers={[title.replace('By ', ''), 'Value', 'Quantity', 'Entries']} empty={!groups.length}>
    {groups.map(g => <tr key={g.key}><td>{labels[g.key] || g.key}</td><td className="ck-number">{money(g.value)}</td><td>{qtyByUom(g.qty_by_uom)}</td><td className="ck-number">{g.count}</td></tr>)}
  </Table></section>;
}

export function WasteReport({ report }) {
  const t = report.totals;
  return <section className="ck-detail" aria-label="Waste report"><h2>Waste report {report.from_date} to {report.to_date}</h2>
    <dl><dt>Total value</dt><dd>{money(t.value)}</dd>
      <dt>% of purchases</dt><dd>{report.pct_of_purchases == null ? 'Not computable (no priced purchases in range)' : `${(Number(report.pct_of_purchases) * 100).toFixed(2)}%`}</dd>
      <dt>Entries</dt><dd>{t.count}{t.unknown_value_count ? ` (${t.unknown_value_count} without a known value)` : ''}</dd>
      <dt>Purchases value</dt><dd>{money(report.purchases_value)}</dd></dl>
    <BarList title="Value by reason" groups={report.by_reason} labels={REASON_LABELS} />
    <GroupTable title="By source" groups={report.by_source} labels={SOURCE_LABELS} />
    <GroupTable title="By site" groups={report.by_site} />
    <GroupTable title="By item" groups={report.by_item} />
    <GroupTable title={`By ${report.period}`} groups={report.by_period} />
  </section>;
}

export function WasteWorkspace({ scope }) {
  const [caps, setCaps] = useState(null);
  const [proposed, setProposed] = useState([]);
  const [recent, setRecent] = useState([]);
  const [report, setReport] = useState(null);
  const [range, setRange] = useState(() => { const to = new Date(); const from = new Date(to.getTime() - 29 * 86400000); return { from: isoDay(from), to: isoDay(to), period: 'day' }; });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const lifetime = useRef(new AbortController());
  const revision = useRef(0);
  const lock = useRef(false);
  const active = useCallback(() => !lifetime.current.signal.aborted && scopeKey(readCkScope()) === scopeKey(scope), [scope]);
  const config = useCallback(() => requestConfig(scope, lifetime.current.signal), [scope]);
  useEffect(() => { const c = new AbortController(); lifetime.current = c; return () => c.abort(); }, []);
  const load = useCallback(async () => {
    const rev = ++revision.current;
    setLoading(true); setError('');
    try {
      if (!scope.orgId || !scope.locationId || !scope.token) throw new Error('Select an organization and site to open Waste.');
      const c = (await wasteApi.get('/capabilities', config())).data;
      if (!active() || rev !== revision.current) return;
      const permissions = { can_record: c.can_record === true, can_decide: c.can_decide === true, can_view_costs: c.can_view_costs === true, can_view_all_sites: c.can_view_all_sites === true };
      setCaps(permissions);
      const records = (await wasteApi.get('/records', { ...config(), params: { limit: 50 } })).data;
      if (!active() || rev !== revision.current) return;
      if (!Array.isArray(records)) throw new Error('Unexpected response. Reload Waste.');
      setProposed(records.filter(r => r.status === 'proposed'));
      setRecent(records.filter(r => r.status !== 'proposed').slice(0, 20));
      if (permissions.can_view_costs) {
        const result = (await wasteApi.get('/report', { ...config(), params: { from: range.from, to: range.to, period: range.period } })).data;
        if (active() && rev === revision.current) setReport(result);
      }
    } catch (e) {
      if (active() && rev === revision.current) { if ([401, 403].includes(e.response?.status)) setCaps(null); setError(message(e)); }
    } finally { if (active() && rev === revision.current) setLoading(false); }
  }, [scope, config, active, range]);
  useEffect(() => { load(); }, [load]);
  const run = async (method, path, body) => {
    if (lock.current || !active()) return false;
    lock.current = true; setBusy(true); setError(''); setNotice('');
    try {
      const result = await wasteApi[method](path, body, config());
      if (active()) { setNotice(result?.data?.proposed !== undefined ? `Sweep proposed ${result.data.proposed} write-off(s); ${result.data.already_proposed} already queued.` : 'Saved.'); await load(); }
      return true;
    } catch (e) { if (active()) setError(message(e)); return false; }
    finally { lock.current = false; if (active()) setBusy(false); }
  };
  const decide = (wasteId, action) => run('post', `/records/${encodeURIComponent(wasteId)}/${action}`, {});
  const canSeeValue = !!caps?.can_view_costs;
  return <main className="ck-ops" data-testid="waste-screen"><header><h1><Trash2 size={20} /> Waste</h1>
    <button title="Refresh" aria-label="Refresh waste" disabled={busy || loading} onClick={load}><RefreshCw size={18} /></button></header>
    {error && <div className="ck-error" role="alert">{error}</div>}{notice && <div className="ck-notice" role="status">{notice}</div>}
    {!caps && !error && <p role="status">Checking waste access...</p>}
    {caps && <>
      {caps.can_record && <details open><summary>Record waste</summary><RecordWaste run={run} busy={busy} canSeeValue={canSeeValue} /></details>}
      {caps.can_decide && <div className="ck-actions"><button disabled={busy} onClick={() => run('post', '/sweep', {})}>Run expiry sweep</button></div>}
      <ProposedQueue rows={proposed} canDecide={caps.can_decide} busy={busy} decide={decide} canSeeValue={canSeeValue} />
      <section aria-label="Recent waste"><h2>Recent waste</h2><Table headers={['When', 'Item', 'Lot', 'Quantity', 'Reason', 'Status', ...(canSeeValue ? ['Value'] : [])]} empty={!recent.length}>
        {recent.map(r => <tr key={r.waste_id}><td>{(r.occurred_at || r.recorded_at || '').slice(0, 16).replace('T', ' ')}</td><td>{r.item_id}</td><td>{r.lot_id || '-'}</td><td className="ck-number">{r.qty} {r.uom}</td><td>{REASON_LABELS[r.reason] || r.reason}{r.note ? `: ${r.note}` : ''}</td><td>{r.status}</td>{canSeeValue && <td className="ck-number">{money(r.value)}</td>}</tr>)}
      </Table></section>
      {canSeeValue && <section aria-label="Report filters" className="ck-form"><h2>Report</h2><div className="ck-fields">
        <Field label="From" type="date" value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} />
        <Field label="To" type="date" value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} />
        <Field label="Group by" value={range.period} onChange={e => setRange({ ...range, period: e.target.value })} options={[{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }]} />
      </div></section>}
      {canSeeValue && report && <WasteReport report={report} />}
      {loading && <p role="status">Loading waste...</p>}
    </>}
  </main>;
}
